import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DataTable from './common/DataTable';
import DataTableWithFilters from './common/DataTableWithFilters';
import ConversationModal from './common/ConversationModal';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import AsistenteService from '../services/AsistenteService';
import AsistentesClasicosService from '../services/AsistentesClasicosService';
import ThreadsService from '../services/ThreadsService';
import RegionalService from '../services/RegionalService';
import './Reports.css';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import {
  isAdminUser as isAdminUserRole,
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { navigateAdminMenuItem, navigateProfessorMenuItem } from './sidebar/menuConfig';

const isReportsUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return (
    roleName === 'profesor' ||
    roleName.includes('profesor') ||
    roleName.includes('profe') ||
    roleName === 'admin' ||
    roleName.includes('adminarea') ||
    roleName.includes('admin area') ||
    roleName.includes('admin')
  );
};

const isGptClassicAssistant = (assistant) => {
  if (!assistant) return false;
  if (isRagFlowAssistant(assistant)) return false;

  const finalized = assistant.gptClasicoCreacionFinalizada ?? assistant.GPTClasicoCreacionFinalizada;
  return finalized === true ||
    Boolean(assistant.gptClasicoModelo || assistant.GPTClasicoModelo) ||
    Boolean(assistant.gptClasicoNombre || assistant.GPTClasicoNombre) ||
    Boolean(assistant.gptClasicoURLPublica || assistant.GPTClasicoURLPublica);
};

const isRagFlowAssistant = (assistant) => {
  const provider = String(assistant?.proveedorIA || assistant?.ProveedorIA || '').trim().toLowerCase();
  const assistantId = String(
    assistant?.idOpenAIAssistant ||
    assistant?.IdOpenAIAssistant ||
    assistant?.idAsistente ||
    assistant?.IdAsistente ||
    ''
  ).trim().toLowerCase();

  return provider === 'ragflow' ||
    assistantId.startsWith('ave-') ||
    Boolean(
      assistant?.idRagFlowDataset ||
      assistant?.IdRagFlowDataset ||
      assistant?.idRagFlowChat ||
      assistant?.IdRagFlowChat
    );
};

const shouldRenderOneRowPerMessage = (assistant, assistantId = '') => {
  const normalizedId = String(
    assistantId ||
    assistant?.idOpenAIAssistant ||
    assistant?.IdOpenAIAssistant ||
    ''
  ).trim().toLowerCase();

  return isRagFlowAssistant(assistant) || normalizedId.startsWith('ave-');
};

const shouldRenderMessageRows = (assistant, assistantId = '', roleFilter = '') => {
  return shouldRenderOneRowPerMessage(assistant, assistantId) || Boolean(roleFilter);
};

const Reports = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canAccessReports = isReportsUser(user);
  const isAdmin = isAdminUserRole(user);
  const roleLabel = isAdmin ? (String(user?.roleName || user?.nombreRol || user?.role || 'Admin')) : 'Profesor';
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: !authLoading && canAccessReports });
  
  // Estados
  const [asistentes, setAsistentes] = useState([]);
  const [asistentesClasicos, setAsistentesClasicos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [showThreadsView, setShowThreadsView] = useState(false);
  
  const [isGptClasico, setIsGptClasico] = useState(false);
  const [gptClasicoBusqueda, setGptClasicoBusqueda] = useState('');
  const [messageRoleFilter, setMessageRoleFilter] = useState('');
  const [messageDateFilters, setMessageDateFilters] = useState({ fechaDesde: '', fechaHasta: '' });
  const gptClasicoBusquedaRef = useRef('');
  const messageRoleFilterRef = useRef('');
  const messageDateFiltersRef = useRef({ fechaDesde: '', fechaHasta: '' });
  const debounceTimerRef = useRef(null);

  // Estados para mensajes
  const [mensajes, setMensajes] = useState([]);
  const [isLoadingMensajes, setIsLoadingMensajes] = useState(false);
  const [paginacionMensajes, setPaginacionMensajes] = useState({
    paginaActual: 1,
    tamañoPagina: 50,
    total: 0,
    totalPaginas: 0
  });
  const [mensajesStats, setMensajesStats] = useState({
    total: 0,
    user: 0,
    assistant: 0,
    threads: 0
  });
  const isLoadingRef = useRef(false);
  const [analisisDocente, setAnalisisDocente] = useState(null);
  const [isLoadingAnalisisDocente, setIsLoadingAnalisisDocente] = useState(false);
  const [analisisDocenteError, setAnalisisDocenteError] = useState('');
  const [isAnalisisDocenteOpen, setIsAnalisisDocenteOpen] = useState(true);
  const analisisDocenteRequestRef = useRef(0);
  
  // Estados paral modal de conversación
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const canOpenAIHistory = featureFlags.some(flag => {
    const nombre = flag?.nombre || flag?.Nombre;
    const activo = flag?.activo ?? flag?.Activo;
    return nombre === 'PermiteObtenerHistorialDesdeOpenAI' && Boolean(activo);
  });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  const shouldShowProviderReports = isAdmin || isOpenAIAssistantEnabled || isRagFlowAssistantEnabled;
  const activeRegionalId = RegionalService.getActiveRegionalId();

  const normalizeAsistente = (item) => {
    if (!item) return item;
    const fechaRaw = item.fechaAlta || item.FechaAlta || item.fechaCreacion || item.FechaCreacion;
    const fechaAlta = fechaRaw ? new Date(fechaRaw) : null;
    return {
      ...item,
      idOpenAIAssistant: item.idOpenAIAssistant || item.IdOpenAIAssistant,
      proveedorIA: item.proveedorIA || item.ProveedorIA,
      idRagFlowDataset: item.idRagFlowDataset || item.IdRagFlowDataset,
      idRagFlowChat: item.idRagFlowChat || item.IdRagFlowChat,
      nombreAsistente:
        item.nombreAsistente ||
        item.NombreAsistente ||
        item.gptClasicoNombre ||
        item.GPTClasicoNombre ||
        item.nombre ||
        item.Nombre ||
        '—',
      nombreArea: item.nombreArea || item.NombreArea || item.areaNombre || item.AreaNombre || item.area || item.Area,
      nombreMateria: item.nombreMateria || item.NombreMateria || item.materiaNombre || item.MateriaNombre || item.materia || item.Materia,
      fechaAlta
    };
  };

  const loadAsistentes = async () => {
    if (authLoading) return;
    if (!canAccessReports) {
      setIsLoading(false);
      return;
    }
    // Evitar llamadas duplicadas
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true; // Mover aquí para evitar race conditions
    
    try {
      setIsLoading(true);
      setAlert({ show: false, message: '', type: 'error' });

      const shouldLoadProviderAssistants = isAdmin || isOpenAIAssistantEnabled || isRagFlowAssistantEnabled;
      const [providerResp, clasicosResp, clasicosMateriaResp] = await Promise.all([
        shouldLoadProviderAssistants
          ? AsistenteService.getMisAsistentes().catch((error) => ({ ok: false, error: error.message || String(error) }))
          : Promise.resolve({ ok: true, asistentes: [] }),
        isGptClassicEnabled
          ? AsistentesClasicosService.getMisAsistentes({
              idRegional: RegionalService.getActiveRegionalId(),
              soloFinalizados: true
            }).catch((error) => ({ ok: false, error: error.message || String(error) }))
          : Promise.resolve({ ok: true, asistentes: [] }),
        isGptClassicEnabled && !isAdmin
          ? AsistentesClasicosService.getAsistentesMisMaterias({
              idRegional: RegionalService.getActiveRegionalId()
            }).catch((error) => ({ ok: false, error: error.message || String(error) }))
          : Promise.resolve({ ok: true, asistentes: [] })
      ]);

      if (providerResp.ok && providerResp.asistentes) {
        const providerAssistants = providerResp.asistentes
          .filter((item) => !isGptClassicAssistant(item))
          .filter((item) => {
            if (isRagFlowAssistant(item)) {
              return isRagFlowAssistantEnabled;
            }
            return isAdmin || isOpenAIAssistantEnabled;
          })
          .map(normalizeAsistente);
        setAsistentes(providerAssistants);
      } else {
        setAsistentes([]);
        if (providerResp.error) {
          setAlert({
            show: true,
            message: providerResp.error || 'Error al cargar los asistentes IA',
            type: 'error'
          });
        }
      }

      if (clasicosResp.ok && clasicosResp.asistentes) {
        const propios = clasicosResp.asistentes.map(normalizeAsistente);
        const compartidos = clasicosMateriaResp.ok && Array.isArray(clasicosMateriaResp.asistentes)
          ? clasicosMateriaResp.asistentes.map(normalizeAsistente)
          : [];
        const asistentesPorId = new Map();
        [...propios, ...compartidos].forEach((asistente) => {
          const id = asistente?.idOpenAIAssistant;
          if (id && !asistentesPorId.has(id)) {
            asistentesPorId.set(id, asistente);
          }
        });
        setAsistentesClasicos(Array.from(asistentesPorId.values()));

        if (!clasicosMateriaResp.ok && !isAdmin && clasicosMateriaResp.error) {
          setAlert({
            show: true,
            message: clasicosMateriaResp.error || 'Error al cargar los GPTs compartidos de tus materias',
            type: 'error'
          });
        }
      } else {
        setAsistentesClasicos([]);
        if (clasicosResp.error) {
          setAlert({
            show: true,
            message: clasicosResp.error || 'Error al cargar los asistentes GPT',
            type: 'error'
          });
        }
      }
      
    } catch (error) {
      console.error('❌ Reports - Error al cargar asistentes:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentes([]);
      setAsistentesClasicos([]);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Cargar asistentes al montar el componente
  useEffect(() => {
    if (authLoading) return;
    if (!canAccessReports) {
      setIsLoading(false);
      return;
    }
    isLoadingRef.current = false;
    loadAsistentes();
  }, [canAccessReports, authLoading, isAdmin, isGptClassicEnabled, isOpenAIAssistantEnabled, isRagFlowAssistantEnabled]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleRefresh = () => {
    if (!canAccessReports || authLoading) return;
    isLoadingRef.current = false; // Resetear el flag de carga
    loadAsistentes();
  };

  const handleAdminMenuSelect = (item) => {
    setIsSidebarOpen(false);
    navigateAdminMenuItem(navigate, item.id);
  };

  const handleProfessorMenuSelect = (item) => {
    setIsSidebarOpen(false);
    navigateProfessorMenuItem(navigate, item.id);
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const resetMessageDateFilters = () => {
    const emptyFilters = { fechaDesde: '', fechaHasta: '' };
    setMessageDateFilters(emptyFilters);
    messageDateFiltersRef.current = emptyFilters;
  };

  const resetMessageRoleFilter = () => {
    setMessageRoleFilter('');
    messageRoleFilterRef.current = '';
  };

  const buildMessageDateFiltersForApi = () => {
    const { fechaDesde, fechaHasta } = messageDateFiltersRef.current;
    return {
      fechaDesde: fechaDesde ? `${fechaDesde}T00:00:00` : undefined,
      fechaHasta: fechaHasta ? `${fechaHasta}T23:59:59.999` : undefined
    };
  };

  const validateMessageDateFilters = () => {
    const { fechaDesde, fechaHasta } = messageDateFiltersRef.current;
    if (fechaDesde && fechaHasta && fechaDesde > fechaHasta) {
      setAlert({
        show: true,
        message: 'La fecha desde no puede ser mayor que la fecha hasta.',
        type: 'error'
      });
      return false;
    }

    return true;
  };

  const resetAnalisisDocente = () => {
    analisisDocenteRequestRef.current += 1;
    setAnalisisDocente(null);
    setAnalisisDocenteError('');
    setIsLoadingAnalisisDocente(false);
  };

  const loadAnalisisDocente = async (asistenteId, roleFilter = messageRoleFilterRef.current) => {
    if (!canAccessReports || !asistenteId || !validateMessageDateFilters()) return;

    const requestId = analisisDocenteRequestRef.current + 1;
    analisisDocenteRequestRef.current = requestId;
    setIsLoadingAnalisisDocente(true);
    setAnalisisDocenteError('');
    setAnalisisDocente(null);

    try {
      const response = await ThreadsService.getAnalisisDocenteMensajes(
        asistenteId,
        buildMessageDateFiltersForApi(),
        { filtroRol: roleFilter }
      );

      if (analisisDocenteRequestRef.current !== requestId) return;
      setAnalisisDocente(response);
    } catch (error) {
      if (analisisDocenteRequestRef.current !== requestId) return;
      setAnalisisDocenteError(error.message || 'No pudimos generar el análisis docente.');
    } finally {
      if (analisisDocenteRequestRef.current === requestId) {
        setIsLoadingAnalisisDocente(false);
      }
    }
  };

  const handleMessageDateFilterChange = (field, value) => {
    const nextFilters = {
      ...messageDateFiltersRef.current,
      [field]: value
    };
    setMessageDateFilters(nextFilters);
    messageDateFiltersRef.current = nextFilters;
  };

  const handleMessageRoleFilterChange = (event) => {
    const nextRole = event.target.value;
    setMessageRoleFilter(nextRole);
    messageRoleFilterRef.current = nextRole;

    if (!validateMessageDateFilters()) return;

    const openAIId = selectedAssistant?.idOpenAIAssistant;
    if (!openAIId) return;

    if (isGptClasico) {
      loadMensajesGptClasico(openAIId, 1, gptClasicoBusquedaRef.current, paginacionMensajes.tamañoPagina, nextRole);
    } else {
      loadMensajesAsistente(openAIId, 1, paginacionMensajes.tamañoPagina, selectedAssistant, nextRole);
    }
    loadAnalisisDocente(openAIId, nextRole);
  };

  // Función para manejar la acción de ver mensajes
  const handleViewMessages = async (asistente) => {
    if (!canAccessReports) return;
    setSelectedAssistant(asistente);
    setShowThreadsView(true);

    const openAIId = asistente.idOpenAIAssistant;

    if (!openAIId) {
      setAlert({
        show: true,
        message: 'Error: No se encontró el ID del asistente',
        type: 'error'
      });
      return;
    }

    const esGptClasico = isGptClassicAssistant(asistente);
    setIsGptClasico(esGptClasico);
    setGptClasicoBusqueda('');
    gptClasicoBusquedaRef.current = '';
    resetMessageDateFilters();
    resetMessageRoleFilter();
    resetAnalisisDocente();
    setIsAnalisisDocenteOpen(true);
    loadAnalisisDocente(openAIId, '');

    if (esGptClasico) {
      await loadMensajesGptClasico(openAIId);
    } else {
      await loadMensajesAsistente(openAIId, 1, paginacionMensajes.tamañoPagina, asistente);
    }
  };

  const buildRowsFromThreads = (threads, oneRowPerMessage = false) => {
    if (!Array.isArray(threads)) return [];

    const rows = [];
    threads.forEach(thread => {
      const mensajesThread = Array.isArray(thread.mensajes) ? thread.mensajes : [];
      if (oneRowPerMessage) {
        mensajesThread.forEach((msg) => {
          const rol = String(msg.rol || '').trim().toLowerCase();
          rows.push({
            ...msg,
            threadId: thread.idThread,
            fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
            fechaEnviado: msg.fechaEnvio ? new Date(msg.fechaEnvio) : null,
            fecha: msg.fechaEnvio ? new Date(msg.fechaEnvio) : null,
            mensaje: msg.contenido || '',
            respuesta: '',
            usuario: rol === 'assistant' ? 'Asistente' : 'Estudiante',
            rol
          });
        });
        return;
      }

      for (let i = 0; i < mensajesThread.length; i++) {
        const msg = mensajesThread[i];
        if (msg.rol !== 'user') continue;

        let respuesta = null;
        for (let j = i + 1; j < mensajesThread.length; j++) {
          if (mensajesThread[j].rol === 'assistant') {
            respuesta = mensajesThread[j];
            break;
          }
        }

        rows.push({
          ...msg,
          threadId: thread.idThread,
          fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
          fechaEnviado: msg.fechaEnvio ? new Date(msg.fechaEnvio) : null,
          fecha: msg.fechaEnvio ? new Date(msg.fechaEnvio) : null,
          mensaje: msg.contenido || '',
          respuesta: respuesta?.contenido || '',
          usuario: 'Estudiante',
          rol: msg.rol
        });
      }
    });

    return rows;
  };

  const fetchAllMessagesForExport = async () => {
    const openAIId = selectedAssistant?.idOpenAIAssistant;
    if (!openAIId || !validateMessageDateFilters()) return [];

    const pageSize = 200;
    const exportRows = [];
    let pagina = 1;
    let totalPaginas = 1;

    do {
      if (isGptClasico) {
        const response = await ThreadsService.getGptClasicoMensajes(
          openAIId,
          pagina,
          pageSize,
          gptClasicoBusquedaRef.current,
          buildMessageDateFiltersForApi(),
          messageRoleFilterRef.current
        );
        exportRows.push(...(response.mensajes || []));
        totalPaginas = response.totalPaginas || 1;
      } else {
        const response = await ThreadsService.getThreadsMessagesFromDb(
          openAIId,
          messageRoleFilterRef.current,
          pagina,
          pageSize,
          100,
          buildMessageDateFiltersForApi()
        );
        exportRows.push(...buildRowsFromThreads(
          response.threads || [],
          shouldRenderMessageRows(selectedAssistant, openAIId, messageRoleFilterRef.current)
        ));
        totalPaginas = response.totalPaginas || 1;
      }

      pagina += 1;
    } while (pagina <= totalPaginas);

    return exportRows;
  };

  // Función para cargar los mensajes del asistente
  const loadMensajesAsistente = async (
    asistenteId,
    pagina = 1,
    tamañoPagina = paginacionMensajes.tamañoPagina,
    assistantContext = selectedAssistant,
    roleFilter = messageRoleFilterRef.current
  ) => {
    if (!canAccessReports) {
      setIsLoadingMensajes(false);
      return;
    }
    if (!validateMessageDateFilters()) return;
    setIsLoadingMensajes(true);
    try {
      const response = await ThreadsService.getThreadsMessagesFromDb(
        asistenteId, 
        roleFilter,
        pagina, 
        tamañoPagina, 
        100,
        buildMessageDateFiltersForApi()
      );
      
      const oneRowPerMessage = shouldRenderMessageRows(assistantContext, asistenteId, roleFilter);
      const rows = buildRowsFromThreads(response.threads || [], oneRowPerMessage);
      setMensajes(rows);

      const totalMessages = response.totalMessages || response.total || 0;
      const threadsCount = response.totalThreads || 0;
      const userCount = oneRowPerMessage
        ? rows.filter(row => row.rol === 'user').length
        : rows.length;
      const assistantCount = oneRowPerMessage
        ? rows.filter(row => row.rol === 'assistant').length
        : Math.max(totalMessages - userCount, 0);
      setMensajesStats({
        total: totalMessages,
        user: userCount,
        assistant: assistantCount,
        threads: threadsCount
      });
      
      // Actualizar información de paginación con la nueva estructura
      setPaginacionMensajes({
        paginaActual: response.paginaActual || pagina,
        tamañoPagina: response.tamañoPagina || tamañoPagina,
        total: totalMessages,
        totalPaginas: response.totalPaginas || Math.ceil(totalMessages / (response.tamañoPagina || tamañoPagina)),
        totalThreads: response.totalThreads || 0,
        totalThreadsEnBD: response.totalThreadsEnBD || 0
      });
      
    } catch (error) {
      console.error('Error cargando mensajes:', error);
      setAlert({
        show: true,
        message: 'Error al cargar los mensajes del asistente',
        type: 'error'
      });
      setMensajes([]);
      setPaginacionMensajes(prev => ({
        ...prev,
        total: 0,
        totalPaginas: 0
      }));
      setMensajesStats({ total: 0, user: 0, assistant: 0, threads: 0 });
    } finally {
      setIsLoadingMensajes(false);
    }
  };

  // Función para cargar mensajes GPT Clásico
  const loadMensajesGptClasico = async (
    asistenteId,
    pagina = 1,
    busqueda = '',
    tamañoPagina = paginacionMensajes.tamañoPagina,
    roleFilter = messageRoleFilterRef.current
  ) => {
    if (!canAccessReports) {
      setIsLoadingMensajes(false);
      return;
    }
    if (!validateMessageDateFilters()) return;
    setIsLoadingMensajes(true);
    try {
      const response = await ThreadsService.getGptClasicoMensajes(
        asistenteId,
        pagina,
        tamañoPagina,
        busqueda,
        buildMessageDateFiltersForApi(),
        roleFilter
      );

      setMensajes(response.mensajes || []);
      setMensajesStats({
        total: response.totalMensajes || 0,
        user: 0,
        assistant: 0,
        threads: 0
      });
      setPaginacionMensajes({
        paginaActual: response.paginaActual || pagina,
        tamañoPagina: response.tamañoPagina || tamañoPagina,
        total: response.totalMensajes || 0,
        totalPaginas: response.totalPaginas || 0
      });
    } catch (error) {
      console.error('Error cargando mensajes GPT clásico:', error);
      setAlert({
        show: true,
        message: 'Error al cargar los mensajes del GPT Clásico',
        type: 'error'
      });
      setMensajes([]);
      setPaginacionMensajes(prev => ({ ...prev, total: 0, totalPaginas: 0 }));
      setMensajesStats({ total: 0, user: 0, assistant: 0, threads: 0 });
    } finally {
      setIsLoadingMensajes(false);
    }
  };

  // Función para volver a la vista de asistentes
  const handleBackToAssistants = () => {
    setShowThreadsView(false);
    setSelectedAssistant(null);
    setIsGptClasico(false);
    setGptClasicoBusqueda('');
    gptClasicoBusquedaRef.current = '';
    resetMessageDateFilters();
    resetMessageRoleFilter();
    resetAnalisisDocente();
    setMensajes([]);
    setPaginacionMensajes({
      paginaActual: 1,
      tamañoPagina: 50,
      total: 0,
      totalPaginas: 0
    });
  };

  useEffect(() => {
    if (!canAccessReports || !location?.state) return;
    const assistantFromState = location.state.assistant || null;
    const assistantId = assistantFromState?.idOpenAIAssistant
      || assistantFromState?.IdOpenAIAssistant
      || location.state.assistantId;
    if (!assistantId) return;
    const assistantName = assistantFromState?.nombreAsistente
      || assistantFromState?.nombre
      || assistantFromState?.Nombre
      || location.state.assistantName
      || 'Asistente';
    const assistantPayload = assistantFromState || { idOpenAIAssistant: assistantId, nombreAsistente: assistantName };
    const esGpt = isGptClassicAssistant(assistantPayload);
    setSelectedAssistant(assistantPayload);
    setShowThreadsView(true);
    setIsGptClasico(esGpt);
    resetMessageDateFilters();
    resetMessageRoleFilter();
    resetAnalisisDocente();
    setIsAnalisisDocenteOpen(true);
    loadAnalisisDocente(assistantId, '');
    if (esGpt) {
      loadMensajesGptClasico(assistantId);
    } else {
      loadMensajesAsistente(assistantId, 1, paginacionMensajes.tamañoPagina, assistantPayload);
    }
  }, [canAccessReports, location]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!canAccessReports) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Los reportes están disponibles únicamente para usuarios con rol Profesor o Admin.</p>
        </div>
      </div>
    );
  }

  // Función para manejar cambio de página en mensajes
  const handlePageChange = (nuevaPagina) => {
    if (selectedAssistant && nuevaPagina !== paginacionMensajes.paginaActual) {
      const openAIId = selectedAssistant.idOpenAIAssistant;
      if (openAIId) {
        if (isGptClasico) {
          loadMensajesGptClasico(openAIId, nuevaPagina, gptClasicoBusquedaRef.current, paginacionMensajes.tamañoPagina);
        } else {
          loadMensajesAsistente(openAIId, nuevaPagina, paginacionMensajes.tamañoPagina, selectedAssistant);
        }
      }
    }
  };

  const handleItemsPerPageChange = (nuevoTamañoPagina) => {
    setPaginacionMensajes(prev => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina: nuevoTamañoPagina
    }));

    const openAIId = selectedAssistant?.idOpenAIAssistant;
    if (!openAIId) return;

    if (isGptClasico) {
      loadMensajesGptClasico(openAIId, 1, gptClasicoBusquedaRef.current, nuevoTamañoPagina);
    } else {
      loadMensajesAsistente(openAIId, 1, nuevoTamañoPagina, selectedAssistant);
    }
  };

  const applyMessageDateFilters = () => {
    if (!validateMessageDateFilters()) return;

    const openAIId = selectedAssistant?.idOpenAIAssistant;
    if (!openAIId) return;

    if (isGptClasico) {
      loadMensajesGptClasico(openAIId, 1, gptClasicoBusquedaRef.current, paginacionMensajes.tamañoPagina);
    } else {
      loadMensajesAsistente(openAIId, 1, paginacionMensajes.tamañoPagina, selectedAssistant);
    }
    loadAnalisisDocente(openAIId);
  };

  const clearMessageDateFilters = () => {
    resetMessageDateFilters();
    resetMessageRoleFilter();

    const openAIId = selectedAssistant?.idOpenAIAssistant;
    if (!openAIId) return;

    if (isGptClasico) {
      loadMensajesGptClasico(openAIId, 1, gptClasicoBusquedaRef.current, paginacionMensajes.tamañoPagina, '');
    } else {
      loadMensajesAsistente(openAIId, 1, paginacionMensajes.tamañoPagina, selectedAssistant, '');
    }
    loadAnalisisDocente(openAIId, '');
  };

  // Función para manejar búsqueda en GPT Clásico con debounce
  const handleGptClasicoBusqueda = (e) => {
    const valor = e.target.value;
    setGptClasicoBusqueda(valor);
    gptClasicoBusquedaRef.current = valor;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (selectedAssistant?.idOpenAIAssistant) {
        loadMensajesGptClasico(selectedAssistant.idOpenAIAssistant, 1, valor, paginacionMensajes.tamañoPagina);
      }
    }, 400);
  };

  // Función para manejar la visualización de conversación completa
  const handleViewConversation = (message, source) => {
    setSelectedMessage({ ...message, conversationSource: source });
    setShowConversationModal(true);
  };  // Función para cerrar el modal de conversación
  const handleCloseConversationModal = () => {
    setShowConversationModal(false);
    setSelectedMessage(null);
  };

  // Función para remover etiquetas HTML y mostrar solo texto plano
  const stripHtmlTags = (html) => {
    if (!html) return '';
    
    // Crear un elemento temporal para usar la API del navegador
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Obtener solo el texto sin etiquetas
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Limpiar espacios excesivos y saltos de línea
    return textContent.replace(/\s+/g, ' ').trim();
  };

  const handleBackToList = () => {
    setShowThreadsView(false);
    setSelectedAssistant(null);
    setIsGptClasico(false);
    setGptClasicoBusqueda('');
    gptClasicoBusquedaRef.current = '';
    resetMessageDateFilters();
    resetMessageRoleFilter();
    resetAnalisisDocente();
    setIsAnalisisDocenteOpen(true);
    setMensajes([]);
    setPaginacionMensajes({
      paginaActual: 1,
      tamañoPagina: 50,
      total: 0,
      totalPaginas: 0
    });
    setMensajesStats({ total: 0, user: 0, assistant: 0, threads: 0 });
  };

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(fechaObj.getTime())) return '-';

    return fechaObj.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAssistantTypeLabel = (assistant) => {
    if (isRagFlowAssistant(assistant)) return 'AVE';
    if (isGptClassicAssistant(assistant)) return 'GPT Clásico';
    return 'OpenAI';
  };

  const getMessageUserLabel = (item) => {
    const rol = String(item?.rol || '').trim().toLowerCase();
    if (rol === 'user') return 'Estudiante';
    if (rol === 'assistant') return 'Asistente';
    return item?.usuario || 'Usuario';
  };

  const getRemitenteLabel = (remitente) => {
    const normalizedRemitente = String(remitente || '').trim().toLowerCase();
    if (normalizedRemitente === 'consumidor' || normalizedRemitente === 'user' || normalizedRemitente === 'usuario' || normalizedRemitente === 'estudiante') {
      return 'Consumidor';
    }
    if (normalizedRemitente === 'chat' || normalizedRemitente === 'assistant' || normalizedRemitente === 'asistente') {
      return 'Chat';
    }
    return remitente || '—';
  };

  const getMessagesExportConfig = () => {
    const assistantName = selectedAssistant?.nombreAsistente || 'asistente';
    const assistantType = getAssistantTypeLabel(selectedAssistant);
    const filePrefix = isGptClasico
      ? 'mensajes-gpt-clasico'
      : isRagFlowAssistant(selectedAssistant)
        ? 'mensajes-ragflow'
        : 'mensajes-openai';
    return {
      fileName: `${filePrefix}-${assistantName}`,
      title: `Mensajes ${assistantType} - ${assistantName}`,
      getRows: fetchAllMessagesForExport
    };
  };

  const renderRemitenteBadge = (remitente) => {
    const normalizedRemitente = String(remitente || '').trim().toLowerCase();

    if (normalizedRemitente === 'consumidor' || normalizedRemitente === 'user' || normalizedRemitente === 'usuario' || normalizedRemitente === 'estudiante') {
      return (
        <span className="user-badge consumer">
          <i className="fas fa-user"></i>
          Consumidor
        </span>
      );
    }

    if (normalizedRemitente === 'chat' || normalizedRemitente === 'assistant' || normalizedRemitente === 'asistente') {
      return (
        <span className="user-badge chat">
          <i className="fas fa-comment-dots"></i>
          Chat
        </span>
      );
    }

    if (!normalizedRemitente) {
      return '—';
    }

    return (
      <span className="user-badge unknown">
        <i className="fas fa-question-circle"></i>
        {remitente}
      </span>
    );
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'tipo',
      title: 'Tipo',
      sortable: true,
      align: 'center',
      width: 'sm',
      exportFormatter: (value, item) => getAssistantTypeLabel(item),
      render: (value, item) => {
        if (isRagFlowAssistant(item)) {
          return <span className="user-badge chat" style={{ fontSize: '0.75rem' }}>AVE</span>;
        }
        if (isGptClassicAssistant(item)) {
          return <span className="user-badge assistant" style={{ fontSize: '0.75rem' }}>GPT Clásico</span>;
        }
        return <span className="user-badge student" style={{ fontSize: '0.75rem' }}>OpenAI</span>;
      }
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha,
      exportFormatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      exportable: false,
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-messages"
            title="Ver mensajes del asistente"
            onClick={() => handleViewMessages(item)}
          >
            <i className="fas fa-comments"></i>
          </button>
        </div>
      )
    }
  ];

  // Definir las columnas para la tabla de mensajes
  const columnasMensajes = [
    {
      key: 'fechaEnviado',
      title: 'Fecha de Envío',
      sortable: true,
      align: 'center',
      width: 'md',
      className: 'date-cell',
      exportFormatter: (fecha) => formatFecha(fecha),
      formatter: (fecha) => {
        if (!fecha) return 'N/A';
        const fechaObj = new Date(fecha);
        return fechaObj.toLocaleDateString('es-AR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      key: 'usuario',
      title: 'Tipo de Usuario',
      sortable: true,
      className: 'font-weight-bold user-type-cell',
      exportFormatter: (usuario, item) => getMessageUserLabel(item),
      formatter: (usuario, item) => {
        const rol = item.rol || 'unknown';
        if (rol === 'user') {
          return (
            <span className="user-badge student">
              <i className="fas fa-user-graduate"></i>
              Estudiante
            </span>
          );
        } else if (rol === 'assistant') {
          return (
            <span className="user-badge assistant">
              <i className="fas fa-robot"></i>
              Asistente
            </span>
          );
        }
        return usuario || 'Usuario';
      }
    },
    {
      key: 'mensaje',
      title: 'Mensaje Enviado',
      sortable: false,
      className: 'message-cell',
      exportFormatter: (mensaje) => stripHtmlTags(mensaje || ''),
      formatter: (mensaje) => {
        // Limpiar HTML del mensaje y truncar si es necesario
        const mensajeTexto = stripHtmlTags(mensaje || '');
        return mensajeTexto.length > 200 
          ? `${mensajeTexto.substring(0, 200)}...` 
          : mensajeTexto;
      }
    },
    {
      key: 'respuesta',
      title: 'Respuesta del Asistente',
      sortable: false,
      className: 'message-cell',
      exportFormatter: (mensaje) => stripHtmlTags(mensaje || '') || '-',
      formatter: (mensaje) => {
        const mensajeTexto = stripHtmlTags(mensaje || '');
        return mensajeTexto.length > 200
          ? `${mensajeTexto.substring(0, 200)}...`
          : mensajeTexto || '-';
      }
    },
    {
      key: 'threadId',
      title: 'Thread ID',
      sortable: true,
      align: 'center',
      width: 'sm',
      className: 'thread-id-cell',
      exportFormatter: (threadId) => threadId || 'N/A',
      formatter: (threadId) => {
        if (!threadId) return 'N/A';
        // Mostrar solo los últimos 8 caracteres para que sea más legible
        return threadId.length > 8 ? `...${threadId.slice(-8)}` : threadId;
      }
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'sm',
      className: 'actions-cell',
      exportable: false,
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-view"
            title="Ver conversación completa (BD)"
            onClick={() => handleViewConversation(item, 'db')}
          >
            <i className="fas fa-database"></i>
          </button>
          {canOpenAIHistory && !isRagFlowAssistant(selectedAssistant) && (
            <button
              className="table-btn table-btn-view"
              title="Ver conversación completa (OpenAI)"
              onClick={() => handleViewConversation(item, 'openai')}
            >
              <i className="fas fa-cloud"></i>
            </button>
          )}
        </div>
      )
    }
  ];

  const columnasMensajesPorMensaje = columnasMensajes
    .filter((column) => column.key !== 'respuesta')
    .map((column) => {
      if (column.key === 'fechaEnviado') {
        return { ...column, title: 'Fecha' };
      }
      if (column.key === 'mensaje') {
        return { ...column, title: 'Mensaje' };
      }
      return column;
    });

  // Columnas para mensajes GPT Clásico (sin acciones)
  const columnasGptClasico = [
    {
      key: 'createdAt',
      title: 'Fecha',
      sortable: true,
      align: 'center',
      width: 'md',
      className: 'date-cell',
      exportFormatter: (fecha) => formatFecha(fecha),
      formatter: (fecha) => {
        if (!fecha) return 'N/A';
        const fechaObj = new Date(fecha);
        return fechaObj.toLocaleDateString('es-AR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      key: 'remitente',
      title: 'Remitente',
      sortable: true,
      className: 'font-weight-bold user-type-cell',
      exportFormatter: (remitente) => getRemitenteLabel(remitente),
      formatter: renderRemitenteBadge
    },
    {
      key: 'mensaje',
      title: 'Mensaje',
      sortable: false,
      className: 'message-cell',
      exportFormatter: (mensaje) => stripHtmlTags(mensaje || '') || '—',
      formatter: (mensaje) => {
        const texto = stripHtmlTags(mensaje || '');
        return texto.length > 200 ? `${texto.substring(0, 200)}...` : texto || '—';
      }
    },
    {
      key: 'conversationId',
      title: 'Conversación',
      sortable: true,
      align: 'center',
      width: 'sm',
      className: 'thread-id-cell',
      exportFormatter: (id) => id || 'N/A',
      formatter: (id) => {
        if (!id) return 'N/A';
        return id.length > 8 ? `...${id.slice(-8)}` : id;
      }
    }
  ];

  if (showThreadsView && selectedAssistant) {
    return (
      <div className={`new-dashboard reports ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <i className="fas fa-graduation-cap"></i>
              <span>UTN FRC</span>
            </div>
          </div>
          
          <nav className="sidebar-nav">
            {isAdmin ? (
              <AdminRoleMenuOptions
                user={user}
                isGptClassicEnabled={isGptClassicEnabled}
                isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
                isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
                activeItemId="reportes"
                onSelect={handleAdminMenuSelect}
              />
            ) : (
              <ProfessorMenuOptions
                user={user}
                isGptClassicEnabled={isGptClassicEnabled}
                isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
                isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
                activeItemId="reportes"
                onSelect={handleProfessorMenuSelect}
              />
            )}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="user-avatar">
                <i className="fas fa-user"></i>
              </div>
              <div className="user-info">
                <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                <span className="user-role">{roleLabel}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content - Threads View */}
        <main className="dashboard-main">
          <header className="dashboard-header">
            <div className="header-left">
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                title="Alternar menú lateral"
              >
                <i className="fas fa-bars"></i>
              </button>
              <button 
                className="btn btn-back"
                onClick={handleBackToList}
                title="Volver a la lista"
              >
                <i className="fas fa-arrow-left"></i>
                Volver
              </button>
            </div>
            
          <div className="header-right">
            <ActiveRegionalBadge />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar datos"
              >
                <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
                Actualizar
              </button>
              
              <UserAccountMenu />
            </div>
          </header>

          <div className="dashboard-content">
            <div className="reports-page-heading">
              <h1>
                <i className="fas fa-comments"></i>
                Mensajes: {selectedAssistant.nombreAsistente}
              </h1>
              <p>Historial completo de conversaciones con estudiantes</p>
            </div>

            {/* Alert */}
            {alert.show && (
              <Alert
                title={alert.title}
                message={alert.message}
                type={alert.type}
                onClose={closeAlert}
              />
            )}

            {/* Mensajes Content */}
            <div className="content-card">
              <div className="card-header">
                <div className="card-header-left">
                  <h2 className="card-title">
                    <i className="fas fa-comments"></i>
                    {isGptClasico ? 'Mensajes GPT Clásico' : 'Mensajes de Estudiantes'}
                  </h2>
                  <p className="card-subtitle">
                    {isGptClasico
                      ? <>Mensajes del GPT Clásico <strong>{selectedAssistant.nombreAsistente}</strong></>
                      : <>Mensajes enviados por estudiantes al asistente <strong>{selectedAssistant.nombreAsistente}</strong></>
                    }
                  </p>
                </div>
                <div className="card-header-right">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleBackToAssistants}
                    title="Volver a la lista de asistentes"
                  >
                    <i className="fas fa-arrow-left"></i>
                    Volver
                  </button>
                </div>
              </div>

              {/* Estadísticas de mensajes */}
              {!isLoadingMensajes && (paginacionMensajes.total > 0 || mensajes.length > 0) && (
                <div className="messages-stats">
                  <div className="stat-item">
                    <span className="stat-number">{mensajesStats.total || 0}</span>
                    <span className="stat-label">Total Mensajes</span>
                  </div>
                  {!isGptClasico && (
                    <>
                      <div className="stat-item">
                        <span className="stat-number">{mensajesStats.user || 0}</span>
                        <span className="stat-label">De Estudiantes</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{mensajesStats.assistant || 0}</span>
                        <span className="stat-label">Del Asistente</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">
                          {mensajesStats.threads || new Set(mensajes.map(m => m.threadId)).size}
                        </span>
                        <span className="stat-label">Threads Activos</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="card-content">
                <div className="reports-message-filters">
                  <div className="date-filter-group">
                    <label htmlFor="reportsFechaDesde">Fecha desde</label>
                    <input
                      id="reportsFechaDesde"
                      type="date"
                      className="date-filter-input"
                      value={messageDateFilters.fechaDesde}
                      max={messageDateFilters.fechaHasta || undefined}
                      onChange={(e) => handleMessageDateFilterChange('fechaDesde', e.target.value)}
                    />
                  </div>
                  <div className="date-filter-group">
                    <label htmlFor="reportsFechaHasta">Fecha hasta</label>
                    <input
                      id="reportsFechaHasta"
                      type="date"
                      className="date-filter-input"
                      value={messageDateFilters.fechaHasta}
                      min={messageDateFilters.fechaDesde || undefined}
                      onChange={(e) => handleMessageDateFilterChange('fechaHasta', e.target.value)}
                    />
                  </div>
                  <div className="date-filter-group">
                    <label htmlFor="reportsMessageRole">Origen del mensaje</label>
                    <select
                      id="reportsMessageRole"
                      className="date-filter-input"
                      value={messageRoleFilter}
                      onChange={handleMessageRoleFilterChange}
                      disabled={isLoadingMensajes}
                    >
                      <option value="">Todos</option>
                      <option value="user">Estudiante</option>
                      <option value="assistant">Chat / Asistente</option>
                    </select>
                  </div>
                  <div className="date-filter-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={applyMessageDateFilters}
                      disabled={isLoadingMensajes}
                    >
                      <i className="fas fa-filter"></i>
                      Filtrar
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={clearMessageDateFilters}
                      disabled={isLoadingMensajes || (!messageDateFilters.fechaDesde && !messageDateFilters.fechaHasta && !messageRoleFilter)}
                    >
                      <i className="fas fa-times"></i>
                      Limpiar
                    </button>
                  </div>
                </div>

                <div className={`teacher-insights-panel ${isAnalisisDocenteOpen ? 'is-open' : 'is-collapsed'}`}>
                  <div className="teacher-insights-header">
                    <div>
                      <h3>
                        <i className="fas fa-lightbulb"></i>
                        Análisis docente
                      </h3>
                      <p>Dudas, temas consultados y oportunidades de mejora en el periodo filtrado.</p>
                    </div>
                    <div className="teacher-insights-actions">
                      <button
                        type="button"
                        className="teacher-insights-action"
                        onClick={() => loadAnalisisDocente(selectedAssistant?.idOpenAIAssistant)}
                        disabled={isLoadingAnalisisDocente || !selectedAssistant?.idOpenAIAssistant}
                        title="Regenerar análisis"
                      >
                        <i className={`fas fa-sync-alt ${isLoadingAnalisisDocente ? 'fa-spin' : ''}`}></i>
                      </button>
                      <button
                        type="button"
                        className="teacher-insights-action"
                        onClick={() => setIsAnalisisDocenteOpen((prev) => !prev)}
                        aria-expanded={isAnalisisDocenteOpen}
                        title={isAnalisisDocenteOpen ? 'Ocultar análisis' : 'Mostrar análisis'}
                      >
                        <i className={`fas ${isAnalisisDocenteOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                      </button>
                    </div>
                  </div>

                  {isAnalisisDocenteOpen && (
                    <div className="teacher-insights-content">
                      {isLoadingAnalisisDocente ? (
                        <div className="teacher-insights-loading">
                          <span className="teacher-insights-spinner"></span>
                          <span>Generando análisis con IA...</span>
                        </div>
                      ) : analisisDocenteError ? (
                        <div className="teacher-insights-error">
                          <i className="fas fa-exclamation-circle"></i>
                          <span>{analisisDocenteError}</span>
                        </div>
                      ) : analisisDocente?.analisis ? (
                        <>
                          <div className="teacher-insights-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {analisisDocente.analisis}
                            </ReactMarkdown>
                          </div>
                          <div className="teacher-insights-meta">
                            <span>{analisisDocente.totalMensajesAnalizados || 0} mensajes analizados</span>
                            {analisisDocente.modelo && <span>Modelo: {analisisDocente.modelo}</span>}
                            {analisisDocente.truncadoPorContexto && <span>Contexto recortado por tamaño</span>}
                          </div>
                        </>
                      ) : (
                        <div className="teacher-insights-empty">
                          No hay análisis disponible para los filtros seleccionados.
                        </div>
                      )}
                    </div>
                  )}
                  {!isAnalisisDocenteOpen && isLoadingAnalisisDocente && (
                    <div className="teacher-insights-collapsed-status">
                      <span className="teacher-insights-spinner"></span>
                      <span>Generando análisis...</span>
                    </div>
                  )}
                  {!isAnalisisDocenteOpen && analisisDocente?.analisis && !isLoadingAnalisisDocente && (
                    <div className="teacher-insights-collapsed-status">
                      <i className="fas fa-check-circle"></i>
                      <span>Análisis disponible</span>
                    </div>
                  )}
                </div>

                {isGptClasico && (
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar en mensajes..."
                      value={gptClasicoBusqueda}
                      onChange={handleGptClasicoBusqueda}
                      style={{ maxWidth: '400px', padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem' }}
                    />
                  </div>
                )}
                {isLoadingMensajes ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Cargando mensajes...</p>
                  </div>
                ) : mensajes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-content">
                      <i className="fas fa-inbox empty-state-icon"></i>
                      <h3>No hay mensajes</h3>
                      <p>{isGptClasico ? 'Este GPT Clásico aún no tiene mensajes registrados.' : 'Este asistente aún no ha recibido mensajes de estudiantes.'}</p>
                    </div>
                  </div>
                ) : isGptClasico ? (
                  <DataTableWithFilters
                    data={mensajes}
                    columns={columnasGptClasico}
                    searchPlaceholder=""
                    emptyMessage="No se encontraron mensajes"
                    loading={isLoadingMensajes}
                    externalPagination={true}
                    externalTotalItems={paginacionMensajes.total}
                    externalTotalPages={paginacionMensajes.totalPaginas}
                    externalCurrentPage={paginacionMensajes.paginaActual}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    defaultItemsPerPage={paginacionMensajes.tamañoPagina || 50}
                    exportConfig={getMessagesExportConfig()}
                  />
                ) : (
                  <DataTableWithFilters
                    data={mensajes}
                    columns={shouldRenderMessageRows(selectedAssistant, '', messageRoleFilter) ? columnasMensajesPorMensaje : columnasMensajes}
                    searchPlaceholder="Buscar en mensajes..."
                    emptyMessage="No se encontraron mensajes que coincidan con la búsqueda"
                    loading={isLoadingMensajes}
                    externalPagination={true}
                    externalTotalItems={paginacionMensajes.total}
                    externalTotalPages={paginacionMensajes.totalPaginas}
                    externalCurrentPage={paginacionMensajes.paginaActual}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    defaultItemsPerPage={paginacionMensajes.tamañoPagina || 50}
                    exportConfig={getMessagesExportConfig()}
                  />
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Modal para ver conversación completa (solo OpenAI) */}
        {!isGptClasico && showConversationModal && selectedMessage && (
          <ConversationModal
            isOpen={showConversationModal}
            onClose={handleCloseConversationModal}
            threadId={selectedMessage.threadId}
            assistantName={selectedAssistant?.nombreAsistente || 'Asistente IA'}
            assistantId={selectedAssistant?.idOpenAIAssistant || 'asst_placeholder'}
            useOpenAI={selectedMessage.conversationSource !== 'db'}
            regionalId={activeRegionalId}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`new-dashboard reports ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdmin ? (
            <AdminRoleMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
              isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
              activeItemId="reportes"
              onSelect={handleAdminMenuSelect}
            />
          ) : (
            <ProfessorMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
              isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
              activeItemId="reportes"
              onSelect={handleProfessorMenuSelect}
            />
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{roleLabel}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            
            <UserAccountMenu />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          <div className="reports-page-heading">
            <h1>
              <i className="fas fa-chart-bar"></i>
              Reportes
            </h1>
            <p>Analizá el rendimiento y uso de tus asistentes de IA</p>
          </div>

          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Table */}
          {shouldShowProviderReports && (
            <div className="content-card">
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-robot"></i>
                  Asistentes IA para Reportes
                </h2>
              </div>
              
              <div className="card-content">
                <DataTable
                  data={asistentes}
                  columns={columns}
                  loading={isLoading}
                  loadingMessage="Obteniendo asistentes..."
                  emptyMessage="No hay asistentes IA disponibles para generar reportes."
                  className="reports-table"
                  exportConfig={{
                    fileName: 'asistentes-ia-reportes',
                    title: 'Asistentes IA para Reportes'
                  }}
                />
              </div>
            </div>
          )}

          {isGptClassicEnabled && (
            <div className="content-card" style={{ marginTop: '1.25rem' }}>
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-robot"></i>
                  Asistentes GPT para Reportes
                </h2>
              </div>

              <div className="card-content">
                <DataTable
                  data={asistentesClasicos}
                  columns={columns}
                  loading={isLoading}
                  loadingMessage="Obteniendo asistentes GPT..."
                  emptyMessage="No hay asistentes GPT disponibles para generar reportes."
                  className="reports-table"
                  exportConfig={{
                    fileName: 'asistentes-gpt-reportes',
                    title: 'Asistentes GPT para Reportes'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal para ver conversación completa */}
      {showConversationModal && selectedMessage && (
        <ConversationModal
          isOpen={showConversationModal}
          onClose={handleCloseConversationModal}
          threadId={selectedMessage.threadId}
          assistantName={selectedAssistant?.nombreAsistente || 'Asistente IA'}
          assistantId={selectedAssistant?.idOpenAIAssistant || 'asst_placeholder'}
        />
      )}
    </div>
  );
};

export default Reports;
